/**
 * Several connections to one backup source.
 *
 * An MSP with sites in two cities has two ShadowControl appliances; one that
 * has made an acquisition can hold a second Datto tenant. Until now the
 * product could only store one credential per source.
 *
 * THE NAME IS THE POINT, not a nicety. It is carried onto every machine that
 * connection finds, so when an appliance goes dark the board says which box -
 * rather than showing thirty machines going quiet for no visible reason.
 *
 * THE ORIGINAL CONNECTION CANNOT BE REMOVED. Its machines are the ones whose
 * ids carry no suffix - every row that predates connections entirely - and
 * deleting it would orphan them. Clearing its credentials is what "stop using
 * this" means for the original, and the Clear button already does that.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { errText } from '../btApi';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

export interface Connection {
  connectionKey: string;
  label: string;
  enabled: boolean;
  baseUrl: string;
  lastTestOk: boolean | null;
  lastTestAt: string | null;
}

const listOrgConnections = call<{ orgId: string }, { connections: Record<string, Connection[]> }>('listOrgConnections');
const addOrgConnection = call<{ orgId: string; source: string; label: string }, { connectionKey: string }>('addOrgConnection');
const deleteOrgConnection = call<{ orgId: string; source: string; connectionKey: string }, { ok: boolean }>('deleteOrgConnection');

export const DEFAULT_CONNECTION = 'default';

export function useConnections(orgId: string, source: string) {
  const [list, setList] = useState<Connection[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try { setList((await listOrgConnections({ orgId })).connections[source] ?? []); }
    catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { void load(); }, [orgId, source]);

  return { list, err, reload: load };
}

export function ConnectionPicker({ orgId, source, sourceLabel, selected, onSelect, onChanged }: {
  orgId: string;
  source: string;
  sourceLabel: string;
  selected: string;
  onSelect: (key: string) => void;
  onChanged: () => void;
}) {
  const { list, err, reload } = useConnections(orgId, source);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  // One connection and it is the original: this is the state every org is in
  // today, and showing a picker for a single unnamed thing is noise. The Add
  // button stays, because that is how the second one gets created.
  const onlyDefault = list.length <= 1;

  async function add() {
    setBusy(true); setLocalErr(null);
    try {
      const r = await addOrgConnection({ orgId, source, label: name.trim() });
      setName(''); setAdding(false);
      await reload();
      onChanged();
      onSelect(r.connectionKey);
    } catch (e) { setLocalErr(errText(e)); }
    finally { setBusy(false); }
  }

  async function remove(key: string, label: string) {
    if (!window.confirm(
      `Remove the "${label}" connection to ${sourceLabel}?\n\n` +
      'Its credentials are deleted. The machines it found are left on the board and ' +
      'will age into missing on their own, rather than disappearing silently.'
    )) return;
    setBusy(true); setLocalErr(null);
    try {
      await deleteOrgConnection({ orgId, source, connectionKey: key });
      if (selected === key) onSelect(DEFAULT_CONNECTION);
      await reload();
      onChanged();
    } catch (e) { setLocalErr(errText(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="conns">
      {(err || localErr) && <div className="banner banner-error">{localErr ?? err}</div>}

      {!onlyDefault && (
        <>
          <div className="field-label">Connection</div>
          <div className="conn-list">
            {list.map((c) => (
              <div key={c.connectionKey} className={'conn' + (selected === c.connectionKey ? ' on' : '')}>
                <button type="button" className="conn-pick" onClick={() => onSelect(c.connectionKey)}>
                  <span className="conn-name">{c.label}</span>
                  {c.lastTestOk === true && <span className="tag tag-ok">test passed</span>}
                  {c.lastTestOk === false && <span className="tag tag-bad">test failed</span>}
                  {!c.enabled && <span className="tag tag-warn">disabled</span>}
                </button>
                {c.connectionKey !== DEFAULT_CONNECTION && (
                  <button
                    type="button" className="btn-link danger" disabled={busy}
                    onClick={() => void remove(c.connectionKey, c.label)}
                  >
                    remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {adding ? (
        <div className="row" style={{ gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field-label">Name this connection</span>
            <input
              className="field-input" value={name} autoFocus
              placeholder="Dallas appliance"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) void add(); }}
            />
          </label>
          <button className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void add()}>
            {busy ? 'Adding…' : 'Add'}
          </button>
          <button className="btn" disabled={busy} onClick={() => { setAdding(false); setName(''); }}>
            Cancel
          </button>
        </div>
      ) : (
        <button className="btn-link" style={{ marginTop: 6 }} onClick={() => setAdding(true)}>
          + Add another {sourceLabel} connection
        </button>
      )}

      {!onlyDefault && (
        <div className="field-hint" style={{ marginTop: 6 }}>
          The name is carried onto every machine this connection finds, so an appliance
          going quiet is traceable to the box.
        </div>
      )}
    </div>
  );
}
